import React, { useState, useEffect, useRef } from 'react';
import { Box, styled, Typography } from '@mui/material';

const NeonContainer = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #0a0a1a 0%, #0f1a1a 50%, #0a0a1a 100%)',
  overflow: 'hidden',
  fontFamily: '"Arial", sans-serif',
});

const ProgressContainer = styled(Box)({
  position: 'relative',
  width: '80%',
  maxWidth: '600px',
  height: '60px',
  background: 'rgba(10, 15, 30, 0.9)',
  borderRadius: '30px',
  border: '2px solid rgba(0, 80, 120, 0.3)',
  overflow: 'hidden',
  boxShadow: `
    0 0 20px rgba(0, 50, 150, 0.4),
    inset 0 0 20px rgba(0, 80, 120, 0.2)
  `,
});

const ProgressBar = styled(Box)({
  position: 'absolute',
  top: '4px',
  left: '4px',
  height: '52px',
  background: 'linear-gradient(90deg, #001f3f 0%, #004d66 50%, #006644 100%)',
  borderRadius: '26px',
  transition: 'width 0.1s linear',
  boxShadow: `
    0 0 10px #001f3f,
    0 0 20px #004d66,
    0 0 30px #006644,
    0 0 40px #004d66
  `,
  animation: 'pulse 2s infinite alternate',
});

const GlowEffect = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(90deg, transparent 0%, rgba(0, 100, 150, 0.3) 50%, transparent 100%)',
  animation: 'shimmer 3s infinite linear',
});

const PercentageText = styled(Typography)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  color: '#e0f7fa',
  fontSize: '1.5rem',
  fontWeight: 'bold',
  textShadow: `
    0 0 10px #001f3f,
    0 0 20px #004d66,
    0 0 30px #006644
  `,
  fontFamily: '"Orbitron", monospace',
});

const LoadingText = styled(Typography)({
  color: '#e0f7fa',
  fontSize: '2rem',
  fontWeight: 'bold',
  marginBottom: '40px',
  textAlign: 'center',
  textShadow: `
    0 0 10px #004d66,
    0 0 20px #006644,
    0 0 30px #004d66
  `,
  fontFamily: '"Orbitron", monospace',
  animation: 'textGlow 2s infinite alternate',
});

const Particle = styled(Box)({
  position: 'absolute',
  width: '4px',
  height: '4px',
  background: '#004d66',
  borderRadius: '50%',
  boxShadow: '0 0 8px #004d66, 0 0 16px #006644',
});

const NeonProgressBar = ({ onComplete, onTransitionStart }) => {
  const [progress, setProgress] = useState(0);
  const [containerOpacity, setContainerOpacity] = useState(1);
  const particlesRef = useRef([]);
  const transitionStartedRef = useRef(false);

  useEffect(() => {
    const duration = 3000; // 3 seconds total
    const interval = 30; // Update every 30ms for smooth animation
    const increment = (interval / duration) * 100;
    
    const timer = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + increment;
        
        // Start transition at 66% (last second for 3-second duration)
        if (newProgress >= 86 && !transitionStartedRef.current) {
          transitionStartedRef.current = true;
          // Start fade out transition for progress bar
          setContainerOpacity(0);
          // Notify parent that transition should start
          if (onTransitionStart) {
            onTransitionStart();
          }
        }
        
        if (newProgress >= 100) {
          clearInterval(timer);
          // Trigger completion callback when progress reaches 100%
          setTimeout(() => {
            onComplete();
          }, 100); // Small delay to ensure transition is complete
          return 100;
        }
        return newProgress;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [onComplete, onTransitionStart]);

  // Create floating particles
  useEffect(() => {
    const particles = [];
    for (let i = 0; i < 20; i++) {
      particles.push({
        id: i,
        left: Math.random() * 100,
        top: Math.random() * 100,
        animationDelay: Math.random() * 5,
        duration: 3 + Math.random() * 4,
      });
    }
    particlesRef.current = particles;
  }, []);

  return (
    <NeonContainer sx={{ 
      opacity: containerOpacity,
      transition: 'opacity 1s ease-in-out'
    }}>
      {/* Floating particles */}
      {particlesRef.current.map(particle => (
        <Particle
          key={particle.id}
          sx={{
            left: `${particle.left}%`,
            top: `${particle.top}%`,
            animation: `float ${particle.duration}s infinite ${particle.animationDelay}s`,
          }}
        />
      ))}
      
      <LoadingText>
        LOADING
      </LoadingText>
      
      <ProgressContainer>
        <ProgressBar sx={{ width: `${progress}%` }} />
        <GlowEffect />
        <PercentageText variant="h6">
          {Math.round(progress)}%
        </PercentageText>
      </ProgressContainer>
      
      {/* Add CSS animations */}
      <style>
        {`
          @keyframes pulse {
            0% { 
              opacity: 0.8; 
              box-shadow: 
                0 0 10px #001f3f,
                0 0 20px #004d66,
                0 0 30px #006644,
                0 0 40px #004d66;
            }
            100% { 
              opacity: 1; 
              box-shadow: 
                0 0 15px #001f3f,
                0 0 25px #004d66,
                0 0 35px #006644,
                0 0 45px #004d66;
            }
          }
          
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          
          @keyframes textGlow {
            0% { 
              text-shadow: 
                0 0 10px #004d66,
                0 0 20px #006644,
                0 0 30px #004d66; 
            }
            100% { 
              text-shadow: 
                0 0 20px #004d66,
                0 0 30px #006644,
                0 0 40px #004d66; 
            }
          }
          
          @keyframes float {
            0%, 100% { 
              transform: translateY(0px) rotate(0deg); 
              opacity: 0; 
              background: #004d66;
              box-shadow: 0 0 8px #004d66, 0 0 16px #006644;
            }
            50% { 
              transform: translateY(-20px) rotate(180deg); 
              opacity: 1; 
              background: #006644;
              box-shadow: 0 0 12px #006644, 0 0 24px #004d66;
            }
          }
        `}
      </style>
    </NeonContainer>
  );
};

export default NeonProgressBar;